// BFF Authentication - все запросы к Keycloak идут через backend

const API_URL = import.meta.env.VITE_API_URL || '/api/v1'
// Production API URL for direct OAuth flow (bypasses localhost proxy)
const PROD_API_URL = import.meta.env.VITE_PROD_API_URL || 'https://kb.vavt.ru/api/v1'

export interface DepartmentMembership {
  departmentId: string
  departmentName?: string
  roles: string[]
  isPrimary: boolean
}

export interface UserInfo {
  id: string
  email: string
  name: string
  groups: string[]
  roles: string[]
  personId?: string | null
  subordinateIds?: string[]
  // Department multitenancy
  departmentMemberships?: DepartmentMembership[]
  currentDepartmentId?: string
  // Impersonation
  isImpersonation?: boolean
  impersonatedBy?: {
    id: string
    name: string
  }
}

/**
 * Redirect to login page (backend handles Keycloak redirect)
 * Stores current URL to restore after login
 */
export function login(): void {
  // Store current URL to redirect back after auth
  const currentPath = window.location.pathname + window.location.search
  if (currentPath !== '/' && !currentPath.startsWith('/api')) {
    sessionStorage.setItem('auth_redirect', currentPath)
  }

  // For localhost dev, redirect directly to production API with returnTo
  // This is necessary because OAuth cookies don't work through proxy
  if (window.location.hostname === 'localhost') {
    const returnTo = encodeURIComponent(window.location.origin + currentPath)
    window.location.href = `${PROD_API_URL}/auth/login?returnTo=${returnTo}`
  } else {
    window.location.href = `${API_URL}/auth/login`
  }
}

/**
 * Get and clear stored redirect URL
 */
export function getAndClearRedirect(): string | null {
  const redirect = sessionStorage.getItem('auth_redirect')
  sessionStorage.removeItem('auth_redirect')
  return redirect
}

/**
 * Get current user info from session
 */
export async function getCurrentUser(): Promise<UserInfo | null> {
  try {
    const response = await fetch(`${API_URL}/auth/me`, {
      credentials: 'include', // Important: send cookies
    })

    if (!response.ok) {
      if (response.status === 401) {
        return null // Not authenticated
      }
      throw new Error(`HTTP ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Failed to get current user:', error)
    return null
  }
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const user = await getCurrentUser()
  return user !== null
}

/**
 * Logout - clears session and redirects
 */
export async function logout(): Promise<void> {
  try {
    await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    })
  } catch (error) {
    console.error('Logout failed:', error)
  }
  // Redirect to login after logout
  login()
}

/**
 * Check if user has a specific role
 */
export function hasRole(user: UserInfo | null, role: string): boolean {
  return user?.roles.includes(role) || false
}

/**
 * Get access token for API calls (if needed for WebSocket etc.)
 */
export async function getAccessToken(): Promise<string | null> {
  try {
    const response = await fetch(`${API_URL}/auth/token`, {
      credentials: 'include',
    })

    if (!response.ok) {
      return null
    }

    const data = await response.json()
    return data.accessToken
  } catch (error) {
    console.error('Failed to get access token:', error)
    return null
  }
}
